import { Observable, Observer } from 'rxjs';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { OktaAuth } from '@okta/okta-auth-js';
import { environment } from 'src/environments/environment';
import { ErrorService } from './shared/services/error.service';

@Injectable({providedIn: 'root'})
export class OktaAuthService {
  CLIENT_ID = environment.oktaConfig.clientId;
  ISSUER = environment.oktaConfig.issuer;
  LOGIN_REDIRECT_URI = environment.oktaConfig.redirectUri;
  LOGOUT_REDIRECT_URI = environment.oktaConfig.logoutRedirectUri;
  ADMIN_REDIRECT_URI = environment.oktaConfig.adminRedirectUri;

  oktaAuth = new OktaAuth({
    clientId: this.CLIENT_ID,
    issuer: this.ISSUER,
    redirectUri: this.LOGIN_REDIRECT_URI,
    postLogoutRedirectUri: this.LOGOUT_REDIRECT_URI,
    pkce: true,
    tokenManager: {
      storage: 'sessionStorage'
    }
  });

  oktaOrgAuthServer = new OktaAuth({
    clientId: this.CLIENT_ID,
    issuer: this.ISSUER.split("oauth")[0],
    responseType: 'token',
    redirectUri: this.LOGIN_REDIRECT_URI,
    postLogoutRedirectUri: this.LOGOUT_REDIRECT_URI,
    pkce: true,
    tokenManager: {
      storage: 'sessionStorage'
    }
  });

  oktaOrgAdminAuthServer = new OktaAuth({
    clientId: this.CLIENT_ID,
    issuer: this.ISSUER.split("oauth")[0],
    responseType: 'token',
    redirectUri: this.ADMIN_REDIRECT_URI,
    postLogoutRedirectUri: this.LOGOUT_REDIRECT_URI,
    pkce: true,
    tokenManager: {
      storage: 'sessionStorage'
    }
  });


  $isAuthenticated: Observable<boolean>;
  private observer: Observer<boolean>;

  constructor(private router: Router, private errorService: ErrorService) {
    this.oktaAuth.start();
    // Triggered when an OAuthError is returned via the API (typically during token renew)
    this.oktaAuth.tokenManager.on('error', err => {
      if (err && err.errorCode === 'login_required' &&
        err.message === 'The client specified not to prompt, but the user is not logged in.') {
        this.login();
      }
    });

    this.$isAuthenticated = new Observable((observer: Observer<boolean>) => {
      this.observer = observer;
      this.isAuthenticated().then(val => {
        observer.next(val);
      });
    });
  }

  async isAuthenticated() {
    // Checks if there is a current accessToken in the TokenManger.
    return !!(await this.oktaAuth.tokenManager.get('accessToken'));
  }

  login(originalUrl?: string) {
    // Save current URL before redirect
    sessionStorage.setItem('okta-app-url', originalUrl || this.router.url);

    // Launches the login redirect.
    this.oktaAuth.token.getWithRedirect({
      scopes: ['openid', 'email', 'profile', 'dac.admin']
    });
  }

  forceLogin() {
    // Launches the login redirect.
    this.oktaAuth.token.getWithRedirect({
      scopes: ['openid', 'email', 'profile', 'dac.admin']
    });
  }

  getUserDetails() {
    if(this.isAuthenticated()){
      return this.oktaAuth.getUser();
    }
  }

  storeOktaAdminTenantInfo(): Promise<number> {
    return new Promise((resolve,reject) => {
      this.oktaAuth.getUser().then(res => {
        if(!sessionStorage.getItem("okta-tenants")){
          if(res.groups.filter(g => g.startsWith("USERS_")).length > 1){
            let tenantNames = [];
            res.groups.filter(g => g.startsWith("USERS_")).forEach(g => {
              tenantNames.push(g.split('_')[1]);
            });
            sessionStorage.setItem("okta-tenants",JSON.stringify({
              "availableTenants": tenantNames
            }));
          }
        }
        resolve(0);
      });
    });
  }

  storeOktaAdminInfoRedirect(): Promise<number> {
    return new Promise((resolve,reject) => {
      this.oktaAuth.getUser().then(res => {
        if(!sessionStorage.getItem("okta-tenants")){
          if(res.groups.filter(g => g.startsWith("USERS_")).length > 1){
            let tenantNames = [];
            res.groups.filter(g => g.startsWith("USERS_")).forEach(g => {
              tenantNames.push(g.split('_')[1]);
            });
            sessionStorage.setItem("okta-tenants",JSON.stringify({
              "availableTenants": tenantNames
            }));
          }
        }

        // does the user belong to any admin group
        if(res.groups.filter(g => g.startsWith("ADMINS_")).length === 1){
          // getting a token for API management scopes can only be done from the org auth server
          this.oktaOrgAdminAuthServer.token.getWithRedirect({
            scopes: ['okta.users.manage', 'okta.groups.manage']
          })
          .catch(err => console.log(err));
        }else if(res.groups.filter(g => g.startsWith("ADMINS_")).length > 1){
            this.errorService.handlerError("You appear to be an admin on multiple tenants. Please contact Service Management");
        }

        resolve(0);
      });
    });
  }


  getAccessToken(): string {
    return this.oktaAuth.getAccessToken();
  }

  getToken(token: string): Promise<any>{
    return this.oktaAuth.tokenManager.get(token);
  }

  async handleAuthentication() {
    return this.oktaAuth.token.parseFromUrl().then(tokenContainer => {
      this.oktaAuth.tokenManager.add('idToken', tokenContainer.tokens.idToken);
      this.oktaAuth.tokenManager.add('accessToken', tokenContainer.tokens.accessToken);

      if (this.isAuthenticated()) {
        this.observer.next(true);
      }

      // Retrieve the saved URL and navigate back
      const url = sessionStorage.getItem('okta-app-url');
      this.router.navigateByUrl(url);
      return true;
    }).catch(err => {
      if (err &&
        (err.errorSummary.includes('Unable to parse a token from the url') ||
          err.errorSummary.includes('User is not assigned to the client application') )) {
        this.observer.next(false);
        this.router.navigateByUrl('/not-assigned');
      }
      return false;
    });
  }

  async logout() {
    this.oktaAuth.stop();
    await this.oktaAuth.signOut();
    this.oktaAuth.tokenManager.clear();

    // delete all session keys
    let storageKeys = Object.keys(sessionStorage);
    storageKeys.forEach(k => sessionStorage.removeItem(k));
  }
}
